import { Chip, CHIP_SIZE, CHIP_TYPE } from '@repo/design-system/index'
import { QIcon_bookmark } from '@repo/icons/index'
import classNames from 'classnames'
import Image from 'next/image'
import { useRouter } from 'next/router'
import React from 'react'
import { getEventUserType, GTM_CLICK_EVENT, sendGTMCustomClickEvent } from '../../../../helpers/gtmHelper'
import { useCurrentUser } from '../../../../modules/context/UserContext'
import { s_lesson_edit } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { DATA_REF_KEY } from '../../../../modules/ids/e2e/student'
import { STUDENT_BTN_ID } from '../../../../modules/ids/gtm/student'
import { COLORS } from '../../../../modules/vars'

interface SelectMaterialCardType {
  title: string
  subtitle: string
  is_mine: boolean
  category: string
  image_url: string
  is_revised: boolean
  is_today: boolean
  level?: number
  audioLength?: number
  className?: string
  onClickHeart?: (e: React.MouseEvent) => void
  onClickSelect?: (e: React.MouseEvent) => void
  selected?: boolean
  horizontal?: boolean
  isList?: boolean
  hashtag?: string
  noCursor?: boolean
}

const SelectMaterialCard: React.FC<SelectMaterialCardType> = ({
  title,
  subtitle,
  is_mine,
  category,
  image_url,
  is_revised,
  is_today,
  level = 0,
  audioLength = 1,
  className = '',
  onClickHeart = () => {},
  onClickSelect = () => {},
  selected = false,
  horizontal = false,
  isList = false,
  hashtag = '',
  noCursor,
}) => {
  const router = useRouter()

  const { currentUser } = useCurrentUser()
  const { t } = useTranslation()

  const sendEvent = (eventType, btnId, eventName) => {
    sendGTMCustomClickEvent(
      eventType,
      btnId,
      eventName,
      getEventUserType(currentUser),
      null, // 공통 컴포넌트이기에 자동 산출로 null 값 설정
      router.asPath,
      {
        material_name: title,
        material_category: category,
        is_bookmarked: is_mine,
      }
    )
  }

  const handleHeartClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()

    sendEvent(GTM_CLICK_EVENT.CLICK_BOOKMARK_MATERIAL, STUDENT_BTN_ID.CLICK_BOOKMARK_MATERIAL, 'Material Card Heart')
    onClickHeart && onClickHeart(e)
  }

  const handleSelectClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    sendEvent(GTM_CLICK_EVENT.CLICK_SELECT_MATERIAL, STUDENT_BTN_ID.CLICK_SELECT_MATERIAL, 'Material Card Detail')
    onClickSelect && onClickSelect(e)
  }

  const getLevel = (level = 0) => {
    switch (level) {
      case 0:
        return 'Basic'
      case 1:
        return 'Intermediate'
      case 2:
        return 'Advanced'
      case 3:
        return 'All'
    }
  }

  if (horizontal) {
    return (
      <div
        className={classNames('relative', {
          'cursor-pointer': !noCursor,
          'cursor-default': noCursor,
        })}
      >
        <div
          className={classNames(
            [className, { 'border-1 border-gray-100': !selected }, { 'border-1 border-purple-500': selected }],
            'relative flex flex-row overflow-hidden rounded-[8px] bg-white'
          )}
        >
          <div className="block-copy-container min-w-[126px] overflow-hidden bg-gray-200">
            {image_url && image_url != '' && (
              <Image
                loading="lazy"
                alt="image_material_thumbnail"
                className="object-cover"
                fill
                sizes={'auto'}
                src={image_url}
                style={{ width: '100%', height: '100%' }}
                quality={100}
              />
            )}
          </div>
          {is_revised && (
            <div className="absolute top-8 left-8">
              <Chip type={CHIP_TYPE.ACTIONABLE} size={CHIP_SIZE.SM} customBgColor={COLORS.YELLOW500}>
                <Chip.Label customTxtColor={COLORS.WHITE}>Revised</Chip.Label>
              </Chip>
            </div>
          )}
          {is_today && (
            <div className="absolute top-8 left-8">
              <Chip type={CHIP_TYPE.ACTIONABLE} size={CHIP_SIZE.SM} customBgColor={COLORS.RED400}>
                <Chip.Label customTxtColor={COLORS.WHITE}>Today</Chip.Label>
              </Chip>
            </div>
          )}
          <div
            onClick={onClickHeart ? handleHeartClick : null}
            className="absolute top-[0px] right-[0px] m-[8px] p-[4px]"
          >
            <QIcon_bookmark color={is_mine ? COLORS.RED400 : COLORS.GRAY300} width={24} height={24} />
          </div>
          <div className="truncate-2-lines flex h-[165px] w-full flex-col py-[12px] px-[16px] text-left">
            <div className="flex h-full flex-col justify-between">
              <div className="">
                <div className="mb-[8px] flex text-gray-500">
                  {category || hashtag ? (
                    <div className="Overline-10Bd rounded-[4px] bg-gray-100 py-[2px] px-[6px]">
                      {category || hashtag}
                    </div>
                  ) : (
                    <div className="h-[20px]"></div>
                  )}
                </div>
                <div className="truncate-2-lines Body1-16Bd mb-[8px] break-keep text-black">{title}</div>
                {subtitle && <div className="Body2-14Md line-clamp-1 break-keep text-gray-600">{subtitle}</div>}
              </div>

              <div className="Caption-12Md mt-[12px] truncate break-keep text-gray-300">
                {audioLength > 0 && `${audioLength} min read ·`} {getLevel(level)}
              </div>
            </div>

            {!isList && onClickSelect && (
              <div
                data-ref={DATA_REF_KEY.btn_select_material}
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  handleSelectClick(e)
                }}
                className={classNames('Body2-14Bd absolute right-[16px] bottom-[12px] select-none text-purple-600')}
              >
                {selected ? t(s_lesson_edit.cancel_material) : t(s_lesson_edit.select_material)}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  } else {
    return (
      <div
        className={classNames(
          isList ? 'lg:w-[calc(100%_-_16px)]' : 'lg:w-[221px]',
          {
            'cursor-pointer': !noCursor,
            'cursor-default': noCursor,
          },
          'sm:max-w-auto relative w-[200px] cursor-pointer sm:w-auto '
        )}
      >
        <div
          className={classNames(
            [className, { 'border-1 border-gray-100': !selected }, { 'border-1 border-purple-500': selected }],
            'relative h-auto overflow-hidden rounded-[8px] bg-white'
          )}
        >
          <div className="block-copy-container h-[113px] min-w-[200px] overflow-hidden bg-gray-200 sm:h-[169px] sm:min-w-[252px]">
            {image_url && image_url != '' && (
              <Image
                loading="lazy"
                alt={title}
                className="object-cover"
                src={image_url || ''}
                fill
                // sizes={'auto'}
                style={{ width: '100%', height: '100%' }}
                quality={100}
              />
            )}
          </div>
          {is_revised && (
            <div className="absolute top-12 left-12">
              <Chip type={CHIP_TYPE.ACTIONABLE} size={CHIP_SIZE.SM} customBgColor={COLORS.YELLOW500}>
                <Chip.Label customTxtColor={COLORS.WHITE}>Revised</Chip.Label>
              </Chip>
            </div>
          )}
          {is_today && (
            <div className="absolute top-12 left-12">
              <Chip type={CHIP_TYPE.ACTIONABLE} size={CHIP_SIZE.SM} customBgColor={COLORS.RED400}>
                <Chip.Label customTxtColor={COLORS.WHITE}>Today</Chip.Label>
              </Chip>
            </div>
          )}
          <div
            onClick={onClickHeart ? handleHeartClick : null}
            className="absolute top-[0px] right-[0px] m-[8px] rounded-full bg-gray-300 bg-opacity-50 p-[4px]"
          >
            <QIcon_bookmark color={is_mine ? COLORS.RED400 : COLORS.GRAY300} width={24} height={24} />
          </div>
          <div className="truncate-2-lines flex h-auto flex-col py-[12px] px-[16px] text-left">
            <div className="mb-[8px] flex text-gray-500">
              {category || hashtag ? (
                <div className="Overline-10Bd rounded-[4px] bg-gray-100 py-[2px] px-[6px]">{category || hashtag}</div>
              ) : (
                <div className="h-[20px]"></div>
              )}
            </div>
            <div className="truncate-2-lines Body1-16Bd mb-[8px] min-h-[52px] break-keep text-black">{title}</div>
            {subtitle && <div className="Body2-14Md truncate break-keep text-gray-600">{subtitle}</div>}
            <div className="Caption-12Md mt-[12px] truncate break-keep text-gray-300">
              {audioLength > 0 && `${audioLength} min read ·`} {getLevel(level)}
            </div>
          </div>
          {!isList && onClickSelect && (
            <div
              data-ref={DATA_REF_KEY.btn_select_material}
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                handleSelectClick(e)
              }}
              className={classNames(
                [{ 'hover:bg-purple-50': !selected }],
                'border-t-1 Body2-14Bd flex h-[48px] items-center justify-center text-purple-600'
              )}
            >
              {selected ? t(s_lesson_edit.cancel_material) : t(s_lesson_edit.select_material)}
            </div>
          )}
        </div>
      </div>
    )
  }
}

export default SelectMaterialCard

interface ISelectMaterialCardSkeleton {
  horizontal?: boolean
}
export const SelectMaterialCardSkeleton = ({ horizontal = false }: ISelectMaterialCardSkeleton) => {
  if (horizontal) {
    return (
      <div className="relative w-full animate-pulse">
        <div className={'border-1 relative flex flex-row overflow-hidden rounded-[8px] bg-white'}>
          <div className="block-copy-container h-[168px] min-w-[130px] overflow-hidden bg-gray-100"></div>

          <div className="absolute top-[0px] right-[0px] m-[8px] p-[4px]"></div>
          <div className="truncate-2-lines flex h-[134px] flex-col py-[12px] px-[16px] text-left">
            <div className="mb-[8px] flex text-gray-500">
              <div className="Overline-10Bd h-[20px] w-[79px] rounded-[4px] bg-gray-100 py-[2px] px-[6px]"></div>
            </div>
            <div className="truncate-2-lines Body1-16Bd mb-[8px] h-[52px] w-[187px] rounded-[4px] bg-gray-100 text-black"></div>
            <div className="h-[22px] w-[100px] rounded-[4px] bg-gray-100 text-gray-600"></div>
          </div>
        </div>
      </div>
    )
  } else {
    return (
      <div className="sm:max-w-auto relative animate-pulse sm:w-auto">
        <div className={'border-1 relative mb-[6px] h-[342px] overflow-hidden rounded-[8px] bg-white'}>
          <div className="block-copy-container h-[160px] w-[219] overflow-hidden bg-gray-100"></div>
          <div className="truncate-2-lines flex h-[134px] flex-col py-[12px] px-[16px] text-left">
            <div className="mb-[8px] flex text-gray-500">
              <div className="Overline-10Bd h-[20px] w-[79px] rounded-[4px] bg-gray-100 py-[2px] px-[6px]"></div>
            </div>
            <div className="truncate-2-lines Body1-16Bd mb-[8px] h-[52px] w-[187px] rounded-[4px] bg-gray-100 text-black"></div>
            <div className="h-[22px] w-[100px] rounded-[4px] bg-gray-100 text-gray-600"></div>
          </div>
          <div className="border-t-1 Body2-14Bd flex h-[48px] items-center justify-center text-purple-600">
            <div className="h-[22px] w-[52px] rounded-[4px] bg-gray-100"></div>
          </div>
        </div>
      </div>
    )
  }
}
